import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/services/api";
import {
  ArrowLeft,
  Package,
  CheckCircle2,
  Clock,
  Truck,
  XCircle,
  MapPin,
  Calendar,
  DollarSign,
  User,
} from "lucide-react";

const STATUS_CONFIG = {
  recibido: { label: "Recibido", color: "bg-blue-100 text-blue-700", icon: Clock },
  confirmado: { label: "Confirmado", color: "bg-indigo-100 text-indigo-700", icon: CheckCircle2 },
  en_produccion: { label: "En Producción", color: "bg-purple-100 text-purple-700", icon: Package },
  en_ruta: { label: "En Ruta", color: "bg-amber-100 text-amber-700", icon: Truck },
  entregado: { label: "Entregado", color: "bg-green-100 text-green-700", icon: CheckCircle2 },
  cancelado: { label: "Cancelado", color: "bg-red-100 text-red-700", icon: XCircle },
};

export default function EmbajadorPedidos() {
  const navigate = useNavigate();
  const [pedidos, setPedidos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPedidos = async () => {
      try {
        const res = await api.get("/embajadores/me/pedidos");
        setPedidos(res.data);
      } catch (error) {
        toast.error("Error al cargar pedidos");
      } finally {
        setLoading(false);
      }
    };
    fetchPedidos();
  }, []);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("es-CO", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <button
            onClick={() => navigate("/embajador/dashboard")}
            className="flex items-center gap-2 text-white/80 hover:text-white mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Volver al dashboard
          </button>
          <h1 className="text-2xl font-bold">Mis Pedidos</h1>
          <p className="text-white/80 mt-1">
            Pedidos generados con tu link de embajador
          </p>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="max-w-4xl mx-auto px-4 -mt-4 mb-6">
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-xl p-4 shadow-md text-center">
            <p className="text-2xl font-bold text-gray-900">{pedidos.length}</p>
            <p className="text-xs text-gray-500">Total</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-md text-center">
            <p className="text-2xl font-bold text-green-600">
              {pedidos.filter(p => p.estado === "entregado").length}
            </p>
            <p className="text-xs text-gray-500">Entregados</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-md text-center">
            <p className="text-2xl font-bold text-amber-600">
              {pedidos.filter(p => !["entregado", "cancelado"].includes(p.estado)).length}
            </p>
            <p className="text-xs text-gray-500">Pendientes</p>
          </div>
        </div>
      </div>

      {/* Pedidos List */}
      <div className="max-w-4xl mx-auto px-4">
        {pedidos.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">
              Aún no tienes pedidos
            </h3>
            <p className="text-gray-500 mb-4">
              Comparte tu link para empezar a generar pedidos
            </p>
            <Button
              onClick={() => navigate("/embajador/dashboard")}
              className="bg-purple-500 hover:bg-purple-600"
            >
              Ir a compartir
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {pedidos.map((pedido) => {
              const statusConfig = STATUS_CONFIG[pedido.estado] || STATUS_CONFIG.recibido;
              const StatusIcon = statusConfig.icon;
              
              return (
                <div
                  key={pedido.id}
                  className="bg-white rounded-2xl shadow-md border border-gray-100 p-5"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono font-semibold text-gray-900">
                          {pedido.numero_pedido || `#${pedido.id?.slice(-6)}`}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 ${statusConfig.color}`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusConfig.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Calendar className="w-3 h-3" />
                        {formatDate(pedido.fecha_creacion)}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-gray-900">
                        {formatCurrency(pedido.total)}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <User className="w-4 h-4 text-gray-400" />
                      <span>{pedido.cliente_nombre}</span>
                    </div>
                    {pedido.cliente_direccion && (
                      <div className="flex items-start gap-2 text-gray-600">
                        <MapPin className="w-4 h-4 text-gray-400 mt-0.5" />
                        <span>{pedido.cliente_direccion}</span>
                      </div>
                    )}
                  </div>

                  {/* Items preview */}
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="text-xs text-gray-500 mb-2">
                      {pedido.items?.length || 0} producto(s)
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {pedido.items?.slice(0, 3).map((item, idx) => (
                        <span
                          key={idx}
                          className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full"
                        >
                          {item.cantidad}x {item.producto_nombre?.substring(0, 20)}
                        </span>
                      ))}
                      {(pedido.items?.length || 0) > 3 && (
                        <span className="text-xs text-gray-400">
                          +{pedido.items.length - 3} más
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
